import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import {addMonths, eachDayOfInterval, endOfMonth, format, isSameDay, startOfMonth, subMonths} from 'date-fns';
import {uk} from 'date-fns/locale';
import {bookingAPI, inventoryAPI} from '../services/api';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonIcon from '@mui/icons-material/Person';

// Custom function to format month with nominative case and capitalized first letter
const formatMonthYear = (date) => {
  // LLLL gives us the standalone month name (nominative case)
  const monthYear = format(date, 'LLLL yyyy', { locale: uk });
  // Capitalize the first letter
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
};

const InventoryAvailability = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inventoryItem, setInventoryItem] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(id);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [allItemsBookings, setAllItemsBookings] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllItems, setShowAllItems] = useState(false);

  const [selectedDay, setSelectedDay] = useState(null);
  const [fadeIn, setFadeIn] = useState(true);
  const [colorInfoDialogOpen, setColorInfoDialogOpen] = useState(false);

  const fetchAllInventoryItems = useCallback(async () => {
    try {
      const response = await inventoryAPI.getAllItems();
      setInventoryItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load inventory items. Please try again later.');
      console.error(err);
    }
  }, []);

  const fetchInventoryItem = useCallback(async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getItemById(selectedItemId);
      setInventoryItem(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load inventory item. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedItemId]);

  const fetchBookingsForMonth = useCallback(async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);

      const response = await bookingAPI.getBookingsByInventoryItem(selectedItemId);

      // Filter bookings that overlap with the current month
      const relevantBookings = response.data.filter(booking => {
        const pickupDate = new Date(booking.pickupDatetime);
        const returnDate = new Date(booking.returnDatetime);

        return (
          (pickupDate <= end && returnDate >= start) && 
          booking.status === 'APPROVED'
        );
      });

      setBookings(relevantBookings);
    } catch (err) {
      console.error('Failed to fetch bookings', err);
    }
  }, [currentMonth, selectedItemId]);

  const fetchAllItemsBookings = useCallback(async () => {
    if (!showAllItems || inventoryItems.length === 0) return;

    setLoading(true);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const bookingsMap = {};

      // Fetch bookings for each inventory item
      await Promise.all(
        inventoryItems.map(async (item) => {
          try {
            const response = await bookingAPI.getBookingsByInventoryItem(item.id);

            // Filter bookings that overlap with the current month
            bookingsMap[item.id] = response.data.filter(booking => {
              const pickupDate = new Date(booking.pickupDatetime);
              const returnDate = new Date(booking.returnDatetime);

              return (
                  (pickupDate <= end && returnDate >= start) &&
                  booking.status === 'APPROVED'
              );
            });
          } catch (err) {
            console.error(`Failed to fetch bookings for item ${item.id}`, err);
            bookingsMap[item.id] = [];
          }
        })
      );

      setAllItemsBookings(bookingsMap);
    } catch (err) {
      console.error('Failed to fetch all items bookings', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, inventoryItems, showAllItems]);

  useEffect(() => {
    fetchAllInventoryItems();
  }, [fetchAllInventoryItems]);

  useEffect(() => {
    fetchInventoryItem();
  }, [fetchInventoryItem]);

  useEffect(() => {
    if (inventoryItem && !showAllItems) {
      fetchBookingsForMonth();
    }
  }, [inventoryItem, fetchBookingsForMonth, showAllItems]);

  // Update filtered bookings when bookings or selectedDay changes
  useEffect(() => {
    if (selectedDay) {
      // Filter bookings that overlap with the selected day
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);

      const filtered = bookings.filter(booking => {
        const pickupDate = new Date(booking.pickupDatetime);
        const returnDate = new Date(booking.returnDatetime);

        // Check if the booking overlaps with the selected day
        return (pickupDate <= dayEnd && returnDate >= dayStart);
      });

      setFilteredBookings(filtered);
    } else {
      // If no day is selected, show all bookings
      setFilteredBookings(bookings);
    }
  }, [bookings, selectedDay]);

  useEffect(() => {
    if (showAllItems) {
      fetchAllItemsBookings();
    }
  }, [showAllItems, fetchAllItemsBookings, currentMonth]);

  // Update URL when selectedItemId changes
  useEffect(() => {
    if (selectedItemId !== id) {
      navigate(`/inventory/${selectedItemId}/availability`, { replace: true });
    }
  }, [selectedItemId, id, navigate]);

  const handleItemChange = (event) => {
    setSelectedItemId(event.target.value);
    // Reset selected day when changing items
    setSelectedDay(null);
    // Trigger fade animation
    setFadeIn(false);
    setTimeout(() => setFadeIn(true), 150);
  };

  const handleToggleAllItems = (event) => {
    setShowAllItems(event.target.checked);
    // Reset selected day when toggling view
    setSelectedDay(null);
    // Trigger fade animation
    setFadeIn(false);
    setTimeout(() => setFadeIn(true), 150);
  };

  const handleMonthChange = (action) => {
    // Trigger fade animation
    setFadeIn(false);

    setTimeout(() => {
      if (action === 'next') {
        setCurrentMonth(prevMonth => addMonths(prevMonth, 1));
      } else if (action === 'previous') {
        setCurrentMonth(prevMonth => subMonths(prevMonth, 1));
      } else if (action instanceof Date) {
        setCurrentMonth(action);
      }
      setFadeIn(true);
    }, 150);

    // Reset selected day when changing month
    setSelectedDay(null);
  };

  // New handlers for enhanced UX
  const handleDaySelect = (day) => {
    setSelectedDay(isSameDay(day, selectedDay) ? null : day);
  };

  const handleBookNow = () => {
    navigate('/booking/create', { 
      state: { 
        selectedItem: inventoryItem,
        selectedDate: selectedDay
      } 
    });
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getAvailabilityForDay = (day) => {
    // Get the start and end of the day we're checking
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);

    // Check if any approved bookings overlap with this day
    const overlappingBookings = bookings.filter(booking => {
      const pickupDate = new Date(booking.pickupDatetime);
      const returnDate = new Date(booking.returnDatetime);

      // Check if the booking overlaps with any part of this day
      return (pickupDate <= dayEnd && returnDate >= dayStart);
    });

    if (overlappingBookings.length === 0) {
      return {
        status: 'available',
        color: 'success.main',
        text: 'Available',
        bookedQuantity: 0
      };
    }

    // Check for fully booked days - when a booking spans the entire day
    const fullyBookedBookings = overlappingBookings.filter(booking => {
      const pickupDate = new Date(booking.pickupDatetime);
      const returnDate = new Date(booking.returnDatetime);

      // A booking fully covers this day if it starts before or at the beginning of the day
      // and ends after or at the end of the day
      return (pickupDate <= dayStart && returnDate >= dayEnd);
    });

    // Calculate total booked quantity for fully booked bookings
    const fullyBookedQuantity = fullyBookedBookings.reduce((total, booking) => {
      const itemBookings = booking.inventoryItems.filter(item => 
        item.inventoryItemId === parseInt(selectedItemId)
      );

      return total + itemBookings.reduce((sum, item) => sum + item.quantity, 0);
    }, 0);

    // If fully booked quantity is greater than or equal to total quantity, mark as unavailable
    if (inventoryItem && fullyBookedQuantity >= inventoryItem.totalQuantity) {
      return {
        status: 'unavailable',
        color: 'error.main',
        text: 'Fully Booked',
        bookedQuantity: fullyBookedQuantity
      };
    }

    // If we have any overlapping bookings but not fully booked, mark as partially available
    return {
      status: 'partially',
      color: 'warning.main',
      text: 'Partially Available',
      bookedQuantity: fullyBookedQuantity
    };
  };

  const getAvailabilityForDayAndItem = (day, item) => {
    if (!allItemsBookings[item.id]) {
      return {
        status: 'available',
        color: 'success.main',
        text: 'Available',
        bookedQuantity: 0
      };
    }

    // Get the start and end of the day we're checking
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);

    // Check if any approved bookings overlap with this day
    const overlappingBookings = allItemsBookings[item.id].filter(booking => {
      const pickupDate = new Date(booking.pickupDatetime);
      const returnDate = new Date(booking.returnDatetime);

      // Check if the booking overlaps with any part of this day
      return (pickupDate <= dayEnd && returnDate >= dayStart);
    });

    if (overlappingBookings.length === 0) {
      return {
        status: 'available',
        color: 'success.main',
        text: 'Available',
        bookedQuantity: 0
      };
    }

    // Check for fully booked days - when a booking spans the entire day
    const fullyBookedBookings = overlappingBookings.filter(booking => {
      const pickupDate = new Date(booking.pickupDatetime);
      const returnDate = new Date(booking.returnDatetime);

      // A booking fully covers this day if it starts before or at the beginning of the day
      // and ends after or at the end of the day
      return (pickupDate <= dayStart && returnDate >= dayEnd);
    });

    // Calculate total booked quantity for fully booked bookings
    const fullyBookedQuantity = fullyBookedBookings.reduce((total, booking) => {
      const itemBookings = booking.inventoryItems.filter(itemBooking => 
        itemBooking.inventoryItemId === parseInt(item.id)
      );

      return total + itemBookings.reduce((sum, itemBooking) => sum + itemBooking.quantity, 0);
    }, 0);

    // If fully booked quantity is greater than or equal to total quantity, mark as unavailable
    if (item && fullyBookedQuantity >= item.totalQuantity) {
      return {
        status: 'unavailable',
        color: 'error.main',
        text: 'Fully Booked',
        bookedQuantity: fullyBookedQuantity
      };
    }

    // If we have any overlapping bookings but not fully booked, mark as partially available
    return {
      status: 'partially',
      color: 'warning.main',
      text: 'Partially Available',
      bookedQuantity: fullyBookedQuantity
    };
  };

  if (loading && !showAllItems) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          mt: 4 
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
            Завантаження даних...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!inventoryItem && !showAllItems) {
    return (
      <Container maxWidth="md">
        <Paper 
          elevation={3} 
          sx={{ 
            mt: 4, 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            borderRadius: 2
          }}
        >
          <EventBusyIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom align="center">
            Інвентар не знайдено
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Вибачте, але запитаний інвентар не існує або був видалений.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />}
            size="large"
            onClick={() => navigate('/')}
          >
            Назад до інвентаря
          </Button>
        </Paper>
      </Container>
    );
  }

  const days = getDaysInMonth();

  // Helper function to render a small calendar for an inventory item
  const renderSmallCalendar = (item) => {
    return (
      <Box 
        sx={{ 
          mt: 1
        }}
      >
        <Grid container spacing={1}>
          {/* Day names header - abbreviated */}
          <Grid container spacing={1} sx={{ mb: 0.5 }}>
            {['Н', 'П', 'В', 'С', 'Ч', 'П', 'С'].map((day) => (
              <Grid item xs={12/7} key={day}>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.6rem' }}>
                  {day}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Add empty cells for days before the first day of the month */}
          {Array.from({ length: new Date(days[0]).getDay() }).map((_, index) => (
            <Grid item xs={12/7} key={`empty-${index}`}>
              <Box sx={{ height: '23px' }}></Box>
            </Grid>
          ))}

          {/* Actual days of the month */}
          {days.map((day) => {
            // Get availability for this day and item
            const availability = getAvailabilityForDayAndItem(day, item);

            return (
              <Grid item xs={12/7} key={day.toString()}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    textAlign: 'center',
                    bgcolor: 
                      availability.status === 'unavailable' ? 'error.main' : 
                      availability.status === 'partially' ? 'warning.main' : 
                      'success.main',
                    color: 'white',
                    height: '23px',
                    width: '23px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    margin: '0 auto',
                    fontSize: '0.7rem'
                  }}
                >
                  {format(day, 'd')}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Color info dialog
  const handleOpenColorInfoDialog = () => {
    setColorInfoDialogOpen(true);
  };

  const handleCloseColorInfoDialog = () => {
    setColorInfoDialogOpen(false);
  };

  const ColorInfoDialog = () => (
    <Dialog
      open={colorInfoDialogOpen}
      onClose={handleCloseColorInfoDialog}
      aria-labelledby="color-info-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="color-info-dialog-title">
        Пояснення до кольорів
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              bgcolor: 'success.main',
              mr: 2
            }} />
            <Typography variant="body1">
              <strong>Доступно</strong> (всі одиниці вільні) - інвентар повністю доступний для бронювання на цю дату.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              bgcolor: 'warning.main',
              mr: 2
            }} />
            <Typography variant="body1">
              <strong>Частково заброньовано</strong> (деякі одиниці вже зайняті) - частина інвентаря вже заброньована, але ще є доступні одиниці.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              bgcolor: 'error.main',
              mr: 2
            }} />
            <Typography variant="body1">
              <strong>Повністю заброньовано</strong> (всі одиниці зайняті) - весь інвентар заброньований на цю дату.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseColorInfoDialog} color="primary">
          Закрити
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Color info dialog */}
        <ColorInfoDialog />

        {/* Header with navigation and controls */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2,
            background: 'linear-gradient(to right, #f5f5f5, #ffffff)'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                fullWidth
                sx={{ 
                  borderRadius: 4,
                  height: '100%',
                  justifyContent: 'flex-start',
                  pl: 2
                }}
              >
                Назад до інвентаря
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: 'background.paper', 
                borderRadius: 4,
                boxShadow: 1,
                p: 0.5
              }}>
                <Tooltip title="Перегляд всіх предметів">
                  <IconButton 
                    color={showAllItems ? 'primary' : 'default'}
                    onClick={(e) => handleToggleAllItems({ target: { checked: !showAllItems } })}
                    sx={{ borderRadius: 3, mx: 0.5 }}
                  >
                    <GridViewIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              {!showAllItems && (
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="inventory-select-label">Оберіть інвентар</InputLabel>
                  <Select
                    labelId="inventory-select-label"
                    id="inventory-select"
                    value={selectedItemId}
                    label="Оберіть інвентар"
                    onChange={handleItemChange}
                    disabled={loading}
                    sx={{ borderRadius: 4 }}
                  >
                    {inventoryItems.map((item) => (
                      <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Title and info section */}
        <Fade in={fadeIn} timeout={300}>
          <Box>
            {!showAllItems ? (
              <Card elevation={3} sx={{ mb: 3, borderRadius: 2, overflow: 'visible' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                  {/* Full-height image on the left */}
                  <Box 
                    sx={{ 
                      width: { xs: '100%', md: '250px' },
                      height: { xs: '250px', md: '300px' }, // Fixed height for consistency
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderTopLeftRadius: 8,
                      borderBottomLeftRadius: { xs: 0, md: 8 },
                      borderTopRightRadius: { xs: 8, md: 0 },
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      p: 2
                    }}
                  >
                    <img 
                      src={inventoryItem?.photoUrl || 'https://via.placeholder.com/250x300?text=No+Image'} 
                      alt={inventoryItem?.name || 'Inventory item'} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CardContent>
                      <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                          {inventoryItem?.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Chip 
                            icon={<EventAvailableIcon />} 
                            label={`Загальна кількість: ${inventoryItem?.totalQuantity}`} 
                            color="primary" 
                            variant="outlined"
                          />
                          <Tooltip title="Натисніть на день у календарі для детальної інформації">
                            <Chip 
                              icon={<InfoIcon />} 
                              label="Як користуватись" 
                              variant="outlined" 
                              color="secondary"
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<InfoIcon />}
                          onClick={handleOpenColorInfoDialog}
                          size="small"
                          sx={{ 
                            borderRadius: 4,
                            textTransform: 'none',
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }
                          }}
                        >
                          Пояснення до кольорів
                        </Button>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<BookOnlineIcon />}
                        onClick={handleBookNow}
                        size="large"
                        sx={{ 
                          borderRadius: 4,
                          px: 3,
                          height: '48px',
                          whiteSpace: 'nowrap',
                          minWidth: '160px',
                          fontWeight: 500,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        Забронювати
                      </Button>
                    </CardActions>
                  </Box>
                </Box>
              </Card>
            ) : (
              <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h4" component="h1" gutterBottom>
                    Календар доступності всього інвентаря
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Перегляд доступності всіх предметів інвентаря на обраний місяць. 
                    Натисніть на картку предмета для детального перегляду.
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<InfoIcon />}
                      onClick={handleOpenColorInfoDialog}
                      size="small"
                      sx={{ 
                        borderRadius: 4,
                        textTransform: 'none',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      Пояснення до кольорів
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Fade>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setError('')}>
                Закрити
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Month navigation */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <IconButton 
            onClick={() => handleMonthChange('previous')} 
            aria-label="Previous month"
            sx={{ 
              bgcolor: 'background.paper', 
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" sx={{ mx: 3, fontWeight: 'bold', color: 'primary.main' }}>
            {formatMonthYear(currentMonth)}
          </Typography>
          <IconButton 
            onClick={() => handleMonthChange('next')} 
            aria-label="Next month"
            sx={{ 
              bgcolor: 'background.paper', 
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Paper>

        {!showAllItems ? (
          <Grid container spacing={3}>
            {/* Left side - Bookings List */}
            <Grid item xs={12} md={8}>
              <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EventAvailableIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      {selectedDay 
                        ? `Бронювання на ${format(selectedDay, 'd MMMM yyyy', { locale: uk })}`
                        : `Бронювання на ${formatMonthYear(currentMonth)}`
                      }
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip 
                      label={`${filteredBookings.length} ${
                        filteredBookings.length === 1 ? 'бронювання' : 
                        filteredBookings.length > 1 && filteredBookings.length < 5 ? 'бронювання' : 'бронювань'
                      }`} 
                      color="secondary" 
                      size="small"
                      sx={{ 
                        bgcolor: 'white', 
                        color: 'primary.main',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ p: 0 }}>
                  {filteredBookings.length > 0 ? (
                    <Box>
                      {filteredBookings.map((booking) => {
                        // Find the inventory item in this booking
                        const bookingItem = booking.inventoryItems.find(
                          item => item.inventoryItemId === parseInt(selectedItemId)
                        );

                        const pickupDate = new Date(booking.pickupDatetime);
                        const returnDate = new Date(booking.returnDatetime);

                        return (
                          <Box 
                            key={booking.id}
                            sx={{ 
                              p: 2, 
                              borderBottom: '1px solid', 
                              borderColor: 'divider',
                              '&:hover': { 
                                bgcolor: 'action.hover',
                                transition: 'background-color 0.2s'
                              }
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={7}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                  <Avatar 
                                    sx={{ 
                                      bgcolor: 'secondary.main', 
                                      width: 40, 
                                      height: 40,
                                      mr: 2
                                    }}
                                  >
                                    <PersonIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="medium">
                                      {booking.userFullName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {booking.purpose}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={5}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <Chip 
                                    label={`Кількість: ${bookingItem ? bookingItem.quantity : 'N/A'}`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mb: 1 }}
                                  />

                                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      mr: 2
                                    }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Отримання
                                      </Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {format(pickupDate, 'dd.MM.yyyy')}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {format(pickupDate, 'HH:mm')}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Повернення
                                      </Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {format(returnDate, 'dd.MM.yyyy')}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {format(returnDate, 'HH:mm')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 4, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <EventAvailableIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" align="center" gutterBottom>
                        {selectedDay 
                          ? "Немає бронювань на цю дату" 
                          : "Немає бронювань на цей місяць"
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Інвентар повністю доступний для бронювання.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Right side - Calendar */}
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Календар доступності
                  </Typography>

                  {/* Day names header */}
                  <Grid container spacing={1} sx={{ mb: 1, mt: 2 }}>
                    {['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'].map((day) => (
                      <Grid item xs={12/7} key={day}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          fontWeight: 'bold', 
                          fontSize: '0.85rem',
                          color: day === 'Нд' || day === 'Сб' ? 'text.secondary' : 'text.primary'
                        }}>
                          {day}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Calendar grid */}
                  <Box sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      {/* Add empty cells for days before the first day of the month */}
                      {Array.from({ length: new Date(days[0]).getDay() }).map((_, index) => (
                        <Grid item xs={12/7} key={`empty-${index}`}>
                          <Box sx={{ height: '40px' }}></Box>
                        </Grid>
                      ))}

                      {/* Actual days of the month */}
                      {days.map((day) => {
                        const availability = getAvailabilityForDay(day);
                        const isSelected = selectedDay && isSameDay(day, selectedDay);

                        return (
                          <Grid item xs={12/7} key={day.toString()}>
                            <Paper 
                              elevation={isSelected ? 6 : 1} 
                              sx={{ 
                                p: 0.5, 
                                textAlign: 'center',
                                bgcolor: 
                                  availability.status === 'unavailable' ? 'error.main' : 
                                  availability.status === 'partially' ? 'warning.main' : 
                                  'success.main',
                                color: 'white',
                                height: '40px',
                                width: '40px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                margin: '0 auto',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                                '&:hover': {
                                  opacity: 0.9,
                                  transform: 'scale(1.1)',
                                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)'
                                }
                              }}
                              onClick={() => handleDaySelect(day)}
                            >
                              <Typography variant="body2" fontWeight="bold" fontSize="1rem">
                                {format(day, 'd')}
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4, mb: 4 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
                  Завантаження інвентаря...
                </Typography>
              </Box>
            ) : (
              <Fade in={fadeIn} timeout={300}>
                <Box>



                  <Grid container spacing={3}>
                    {inventoryItems.map((item) => {
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                          <Card 
                            elevation={3}
                            sx={{ 
                              height: '100%',
                              cursor: 'pointer',
                              borderRadius: 2,
                              overflow: 'hidden',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
                              }
                            }}
                            onClick={() => {
                              setShowAllItems(false);
                              setSelectedItemId(item.id);
                            }}
                          >
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'primary.main', 
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                {item.name}
                              </Typography>
                              <Chip 
                                label={`${item.totalQuantity} шт.`} 
                                size="small"
                                sx={{ 
                                  bgcolor: 'white', 
                                  color: 'primary.main',
                                  fontWeight: 'bold'
                                }}
                              />
                            </Box>

                            <CardContent sx={{ p: 2 }}>
                              {renderSmallCalendar(item)}
                            </CardContent>

                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Fade>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default InventoryAvailability;
